import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { AlignLeft, Camera, FileText, ListMusic, PlayCircle } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../config/api';
import './SpanishSongsPage.css';

// Every song sung at the 12:30 Spanish Mass, with where it comes from, its credit, how often
// it is sung, a recording to learn it from, its words and its music. The list is
// app/spanish-songs/songs.json (made by make_songs.py); the PDFs are licensed and sent only
// to a signed-in user. Design approved by Jack 2026-11-02 (mockup on the Design canvas);
// round two (most used, service music view, lyrics, new buttons) asked for the same day.

interface LyricSection {
  type: string;
  label: string;
  lines: string[];
}

interface Song {
  id: string;
  title: string;
  alt: string;
  credit: string;
  source: string;
  also: string;
  book: 'fyc' | 'himnario' | 'oc' | 'other';
  section: 'song' | 'service';
  youtube: string | null;
  melody: string | null;
  chords: string | null;
  uses: number;
  last_used: string | null;
  lyrics: LyricSection[] | null;
  lyrics_credit: string;
  needs: string | null;
  needs_order: number | null;
}

type Book = 'all' | Song['book'];
type Sort = 'used' | 'music' | 'title' | 'hymnal';
type View = 'all' | 'songs' | 'service' | 'needs';

const BOOKS: { id: Book; label: string }[] = [
  { id: 'all', label: 'All books' },
  { id: 'fyc', label: 'Flor y Canto' },
  { id: 'himnario', label: 'El Himnario' },
  { id: 'oc', label: 'Oramos Cantando' },
  { id: 'other', label: 'Other' },
];

const VIEWS: { id: View; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'songs', label: 'No service music' },
  { id: 'service', label: 'Only service music' },
  { id: 'needs', label: 'To photograph' },
];

const SORTS: { id: Sort; label: string }[] = [
  { id: 'used', label: 'Most used' },
  { id: 'music', label: 'Sheet music' },
  { id: 'title', label: 'A to Z' },
  { id: 'hymnal', label: 'Hymnal' },
];

// Hymnal order: the books the parish sings from most, then the rest, then no book.
const HYMNAL_ORDER = ['Flor y Canto 2', 'Flor y Canto 3', 'Flor y Canto 4', 'El Himnario', 'Oramos Cantando'];

const titleKey = (s: Song) => s.title.replace(/^[¿¡]/, '');
const byTitle = (a: Song, b: Song) =>
  titleKey(a).localeCompare(titleKey(b), 'es', { sensitivity: 'base' });

function hymnalKey(s: Song): [number, number] {
  const [book, num] = s.source.split(' · ');
  const i = HYMNAL_ORDER.indexOf(book);
  const rank = s.source ? (i >= 0 ? i : HYMNAL_ORDER.length) : HYMNAL_ORDER.length + 1;
  return [rank, num ? parseInt(num, 10) || 0 : 0];
}

const musicRank = (s: Song) => (s.chords ? 0 : s.melody ? 1 : 2);

function sortSongs(list: Song[], sort: Sort): Song[] {
  const out = [...list];
  if (sort === 'title') return out.sort(byTitle);
  if (sort === 'music') return out.sort((a, b) => musicRank(a) - musicRank(b) || byTitle(a, b));
  if (sort === 'used') {
    return out.sort((a, b) => b.uses - a.uses ||
      (b.last_used || '').localeCompare(a.last_used || '') || byTitle(a, b));
  }
  return out.sort((a, b) => {
    const [ra, na] = hymnalKey(a);
    const [rb, nb] = hymnalKey(b);
    const ba = a.source.split(' · ')[0];
    const bb = b.source.split(' · ')[0];
    return ra - rb || ba.localeCompare(bb, 'es') || na - nb || byTitle(a, b);
  });
}

function fileUrl(kind: 'melody' | 'chords', file: string): string {
  // A PDF opened in a new tab cannot send the sign-in header, so the token goes in the address.
  const token = localStorage.getItem('token') || '';
  return `${API_BASE_URL.replace('/api', '')}/api/spanish-songs/files/${kind}/` +
    `${encodeURIComponent(file)}?token=${encodeURIComponent(token)}`;
}

function usedLine(s: Song): string {
  if (!s.uses) return 'Not on record yet';
  const times = s.uses === 1 ? 'Sung once' : `Sung ${s.uses} times`;
  if (!s.last_used) return times;
  const d = new Date(`${s.last_used}T12:00:00`);
  const last = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${times}, last ${last}`;
}

function Segmented<T extends string>(props: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="ss-seg" role="group" aria-label={props.label}>
      {props.options.map(o => (
        <button
          key={o.id}
          type="button"
          className={`ss-seg-btn${props.value === o.id ? ' is-on' : ''}`}
          aria-pressed={props.value === o.id}
          onClick={() => props.onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const SongRow: React.FC<{ song: Song; onLyrics: (s: Song) => void }> = ({ song, onLyrics }) => (
  <div className="ss-row">
    <div className="ss-name">
      <div className="ss-title">
        {song.title}
        {song.alt && <span className="ss-alt"> {song.alt}</span>}
        {song.section === 'service' && <span className="ss-tag">Service music</span>}
      </div>
      {song.credit && <div className="ss-credit">{song.credit}</div>}
      {song.needs && <div className="ss-needs"><Camera size={14} aria-hidden="true" />To photograph: {song.needs}</div>}
    </div>
    <div className="ss-source">
      <div className="ss-book">{song.source || <span className="ss-none">Book not known</span>}</div>
      {song.also && <div className="ss-also">{song.also}</div>}
      <div className={`ss-used${song.uses ? '' : ' is-none'}`}>{usedLine(song)}</div>
    </div>
    <div className="ss-links">
      {song.youtube && (
        <a className="ss-btn ss-btn-listen" href={song.youtube} target="_blank" rel="noopener noreferrer">
          <PlayCircle size={15} aria-hidden="true" />Listen
        </a>
      )}
      {song.lyrics && (
        <button type="button" className="ss-btn ss-btn-lyrics" onClick={() => onLyrics(song)}>
          <AlignLeft size={15} aria-hidden="true" />Lyrics
        </button>
      )}
      {song.melody && (
        <a className="ss-btn ss-btn-melody" href={fileUrl('melody', song.melody)} target="_blank" rel="noopener noreferrer">
          <FileText size={15} aria-hidden="true" />Melody
        </a>
      )}
      {song.chords && (
        <a className="ss-btn ss-btn-chords" href={fileUrl('chords', song.chords)} target="_blank" rel="noopener noreferrer">
          <ListMusic size={15} aria-hidden="true" />Chords
        </a>
      )}
      {!song.melody && !song.chords && <span className="ss-missing">Music coming</span>}
    </div>
  </div>
);

const LyricsModal: React.FC<{ song: Song | null; onClose: () => void }> = ({ song, onClose }) => (
  <Modal show={!!song} onHide={onClose} centered scrollable size="lg" dialogClassName="ss-lyrics-dialog">
    {song && (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="div">
            <div className="ss-lyrics-title">{song.title}</div>
            {song.source && <div className="ss-lyrics-source">{song.source}</div>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ss-lyrics-body">
          {(song.lyrics || []).map((sec, i) => {
            const refrain = sec.type !== 'estrofa';
            return (
              <div key={i} className={`ss-lyr-sec${refrain ? ' is-refrain' : ''}`}>
                {sec.label && (
                  <div className="ss-lyr-label">{sec.type === 'estrofa' ? `${sec.label}.` : sec.label}</div>
                )}
                {sec.lines.map((l, j) => <div key={j} className="ss-lyr-line">{l}</div>)}
              </div>
            );
          })}
          {song.lyrics_credit && <p className="ss-lyr-credit">{song.lyrics_credit}</p>}
          <p className="ss-lyr-credit">Reprinted with permission under ONE LICENSE #A-710678. All rights reserved.</p>
        </Modal.Body>
      </>
    )}
  </Modal>
);

const SpanishSongsPage: React.FC<{ hideNavigation?: boolean }> = ({ hideNavigation }) => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [book, setBook] = useState<Book>('all');
  const [view, setView] = useState<View>('all');
  const [sort, setSort] = useState<Sort>('used');
  const [query, setQuery] = useState('');
  const [lyricsSong, setLyricsSong] = useState<Song | null>(null);

  useEffect(() => {
    apiClient.get('/spanish-songs')
      .then(r => setSongs(r.data?.data?.songs || []))
      .catch(err => setError(err instanceof Error ? err.message : 'Could not load the songs.'))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const keep = (s: Song) => (book === 'all' || s.book === book) &&
      (view === 'all' || (view === 'needs' ? !!s.needs : (view === 'service') === (s.section === 'service'))) &&
      (!q || [s.title, s.alt, s.credit, s.source, s.also].join(' ').toLowerCase().includes(q));
    // The photograph list keeps the order Claude needs them in: last year's Advent first, then the most sung.
    const list = view === 'needs'
      ? songs.filter(keep).sort((a, b) => (a.needs_order ?? 0) - (b.needs_order ?? 0))
      : sortSongs(songs.filter(keep), sort);
    return {
      list,
      withMusic: list.filter(s => s.melody || s.chords).length,
      withLyrics: list.filter(s => s.lyrics).length,
    };
  }, [songs, book, view, sort, query]);

  const body = (
    <div className="ss-page">
      <p className="ss-eyebrow">Songs</p>
      <h1 className="ss-h1">Spanish Songs</h1>
      <p className="ss-lede">
        Cantos de la misa de las 12:30. Every song we sing in Spanish, with its words, the music,
        a recording to learn it from, and where it comes from.
      </p>

      <div className="ss-tools">
        <div className="ss-tools-row">
          <label htmlFor="ss-search" className="visually-hidden">Search songs</label>
          <input
            id="ss-search"
            type="search"
            className="ss-search"
            placeholder="Search titles, composers, numbers"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <Segmented label="Show" options={VIEWS} value={view} onChange={setView} />
        </div>
        <div className="ss-tools-row">
          <span className="ss-tools-label">Sort</span>
          <Segmented label="Sort" options={SORTS} value={sort} onChange={setSort} />
          <span className="ss-tools-label ss-tools-gap">Book</span>
          <Segmented label="Book" options={BOOKS} value={book} onChange={setBook} />
        </div>
        <div className="ss-count">
          {shown.list.length} {shown.list.length === 1 ? 'song' : 'songs'} · {shown.withLyrics} with lyrics ·{' '}
          {shown.withMusic} with music
        </div>
      </div>

      {loading ? (
        <div className="ss-loading"><Spinner animation="border" size="sm" /> Loading songs…</div>
      ) : error ? (
        <Alert variant="danger" className="mt-4">{error}</Alert>
      ) : (
        <>
          <div className="ss-list">
            {shown.list.length
              ? shown.list.map(s => <SongRow key={s.id} song={s} onLyrics={setLyricsSong} />)
              : <p className="ss-empty">No songs match.</p>}
          </div>

          <footer className="ss-foot">
            Music and words reprinted with permission under ONE LICENSE #A-710678. All rights
            reserved. Copyright for each song is shown with it. Times sung count the 12:30 service
            from August 2025 (no record for October 2025); service music was not always recorded,
            so its counts run low. Books: Flor y Canto, 2nd and 3rd editions (OCP); El Himnario
            (Church Publishing); Oramos Cantando (GIA).
          </footer>
        </>
      )}
      <LyricsModal song={lyricsSong} onClose={() => setLyricsSong(null)} />
    </div>
  );

  if (hideNavigation) return body;
  return (
    <>
      <Navigation />
      {body}
    </>
  );
};

export default SpanishSongsPage;
